import { useEffect, useState } from "react";
import { Eye, Download, ShieldCheck, FileText, AlertTriangle, CheckCircle2, Clock, AlertCircle, CalendarClock, FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import api, { formatApiError, fmtEuro, fmtDate, fmtDateTime, isMobileDevice, f24FileUrl } from "@/lib/api";
import ClientLayout from "@/components/ClientLayout";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const MESI = ["", "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function ClientArea() {
  const [items, setItems] = useState(null);
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("tutti");
  const [stato, setStato] = useState("tutti");
  const [categoria, setCategoria] = useState("tutti");
  const [dueFrom, setDueFrom] = useState("");
  const [dueTo, setDueTo] = useState("");
  const [pdfModal, setPdfModal] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [confirmAuth, setConfirmAuth] = useState(null);

  const exportFile = (fmt) => {
    const params = new URLSearchParams();
    if (year !== "tutti") params.set("year", year);
    if (stato !== "tutti") params.set("status", stato);
    if (categoria !== "tutti") params.set("categoria", categoria);
    if (dueFrom) params.set("due_from", dueFrom);
    if (dueTo) params.set("due_to", dueTo);
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/export.${fmt}?${params.toString()}`, "_blank");
  };

  const load = () => {
    const params = year !== "tutti" ? { year } : {};
    api.get("/f24", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => { load(); }, [year]);
  useEffect(() => {
    api.get("/f24/years").then((r) => setYears(r.data)).catch(() => {});
  }, []);

  const openPdf = async (f) => {
    if (isMobileDevice()) {
      window.open(f24FileUrl(f.id), "_blank");
      return;
    }
    setPdfLoading(true);
    setPdfModal({ f, url: null });
    try {
      const r = await api.get(`/f24/${f.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      setPdfModal({ f, url });
    } catch (err) {
      toast.error(formatApiError(err));
      setPdfModal(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadPdf = async (f) => {
    try {
      const r = await api.get(`/f24/${f.id}/file`, { params: { download: true }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = f.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleAuthorize = async () => {
    const { f, authorize } = confirmAuth;
    try {
      await api.post(`/f24/${f.id}/authorize`, { authorize });
      toast.success(authorize ? "Pagamento autorizzato. La conferma è stata registrata." : "Autorizzazione revocata.");
      setConfirmAuth(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
      setConfirmAuth(null);
    }
  };

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  const isScaduto = (f) => f.status !== "pagato" && f.due_date < today;
  const giorniAllaScadenza = (f) =>
    Math.round((new Date(`${f.due_date}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000);
  const categorie = [...new Set((items || []).map((f) => f.categoria || "Altro"))];
  const filtered = items
    ? items.filter(
        (f) =>
          (stato === "tutti" || f.status === stato) &&
          (categoria === "tutti" || (f.categoria || "Altro") === categoria) &&
          (!dueFrom || f.due_date >= dueFrom) &&
          (!dueTo || f.due_date <= dueTo)
      )
    : null;
  const sorted = filtered
    ? [...filtered].sort((a, b) => b.due_date.localeCompare(a.due_date))
    : [];
  const sumAmt = (arr) => arr.reduce((s, f) => s + (f.amount || 0), 0);
  const stats = filtered
    ? {
        pagati: filtered.filter((f) => f.status === "pagato"),
        daConfermare: filtered.filter((f) => f.status === "in_attesa"),
        scaduti: filtered.filter((f) => f.status !== "pagato" && f.due_date < today),
      }
    : null;
  const daConfermare = stats?.daConfermare.length ?? 0;

  const scadenzeMap = {};
  sorted.filter((f) => f.status !== "pagato").forEach((f) => {
    (scadenzeMap[f.due_date] = scadenzeMap[f.due_date] || []).push(f);
  });
  const scadenzeList = Object.entries(scadenzeMap)
    .map(([data, arr]) => ({ data, items: arr, totale: sumAmt(arr) }))
    .sort((a, b) => a.data.localeCompare(b.data));

  const byYear = filtered
    ? Object.values(
        filtered.reduce((acc, f) => {
          const y = String(f.year);
          if (!acc[y]) acc[y] = { year: y, pagato: 0, autorizzato: 0, in_attesa: 0, scaduto: 0 };
          const bucket = f.status !== "pagato" && f.due_date < today ? "scaduto" : f.status;
          acc[y][bucket] = (acc[y][bucket] || 0) + (f.amount || 0);
          return acc;
        }, {})
      ).sort((a, b) => a.year.localeCompare(b.year))
    : [];

  return (
    <ClientLayout title="I tuoi Modelli F24" subtitle="Visualizza, scarica e autorizza il pagamento dei tuoi F24">
      {daConfermare > 0 && (
        <div className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pending-alert">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {daConfermare === 1 ? "Hai 1 F24 in attesa di conferma" : `Hai ${daConfermare} F24 in attesa di conferma`}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              Controlla il documento e spunta la casella di autorizzazione per confermare il pagamento.
              Ricorda: l'autorizzazione può essere revocata online solo fino a 2 giorni prima della scadenza.
            </p>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="client-stats">
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 animate-fade-up" data-testid="client-stat-pagati">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">F24 Pagati</p>
            </div>
            <p className="font-display text-2xl sm:text-3xl font-bold text-navy">{fmtEuro(sumAmt(stats.pagati))}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.pagati.length} documenti</p>
          </div>
          <div className="bg-white rounded-xl border border-amber-200 shadow-sm p-5 animate-fade-up" style={{ animationDelay: "60ms" }} data-testid="client-stat-da-confermare">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-600" />
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Da Confermare</p>
            </div>
            <p className="font-display text-2xl sm:text-3xl font-bold text-amber-600">{fmtEuro(sumAmt(stats.daConfermare))}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.daConfermare.length} in attesa della tua conferma</p>
          </div>
          <div className="bg-white rounded-xl border border-red-200 shadow-sm p-5 animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="client-stat-scaduti">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Scaduti non confermati</p>
            </div>
            <p className="font-display text-2xl sm:text-3xl font-bold text-red-600">{fmtEuro(sumAmt(stats.scaduti))}</p>
            <p className="text-xs text-muted-foreground mt-1">{stats.scaduti.length} scaduti e non pagati</p>
          </div>
        </div>
      )}

      {items && items.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6 mb-8" data-testid="client-chart-card">
          <h2 className="font-display text-xl font-bold text-navy">Importi per anno</h2>
          <p className="text-xs text-muted-foreground mt-0.5 mb-4">I tuoi tributi pagati, autorizzati e in attesa</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byYear} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
              <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${(v / 1000).toFixed(1)}k`} />
              <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="pagato" name="Pagato" stackId="a" fill="#1E4020" />
              <Bar dataKey="autorizzato" name="Autorizzato" stackId="a" fill="#3C763D" />
              <Bar dataKey="in_attesa" name="In attesa" stackId="a" fill="#F59E0B" />
              <Bar dataKey="scaduto" name="Scaduto non pagato" stackId="a" fill="#DC2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {scadenzeList.length > 0 && (
        <div className="mb-8" data-testid="prossime-scadenze-panel">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="w-5 h-5 text-[#3C763D]" />
            <h2 className="font-display text-xl font-bold text-navy">Prossime scadenze</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {scadenzeList.map((g) => {
              const scaduta = g.data < today;
              return (
                <div key={g.data} data-testid={`scadenza-group-${g.data}`} className={`bg-white rounded-xl border shadow-sm p-4 ${scaduta ? "border-red-200" : "border-slate-200/80"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className={`font-bold ${scaduta ? "text-red-600" : "text-navy"}`}>{fmtDate(g.data)}</p>
                    {scaduta && (
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-red-600 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">Scaduta</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    {g.items.length} {g.items.length === 1 ? "delega" : "deleghe"} · {g.items.map((x) => x.categoria || "F24").join(", ")}
                  </p>
                  <p className="font-display text-xl font-bold text-[#3C763D]">{fmtEuro(g.totale)}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-4 mb-6 flex flex-wrap items-end gap-3" data-testid="client-filters">
        <div className="w-32">
          <Label className="text-xs">Anno</Label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="filter-year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-xs">Stato</Label>
          <Select value={stato} onValueChange={setStato}>
            <SelectTrigger data-testid="filter-stato-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti</SelectItem>
              <SelectItem value="in_attesa">In attesa di conferma</SelectItem>
              <SelectItem value="autorizzato">Autorizzato</SelectItem>
              <SelectItem value="pagato">Pagato</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="text-xs">Tipo delega</Label>
          <Select value={categoria} onValueChange={setCategoria}>
            <SelectTrigger data-testid="filter-categoria-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutte</SelectItem>
              {categorie.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Scadenza da</Label>
          <Input type="date" value={dueFrom} onChange={(e) => setDueFrom(e.target.value)} className="w-40" data-testid="filter-due-from" />
        </div>
        <div>
          <Label className="text-xs">a</Label>
          <Input type="date" value={dueTo} onChange={(e) => setDueTo(e.target.value)} className="w-40" data-testid="filter-due-to" />
        </div>
        <p className="text-sm text-muted-foreground ml-auto">{filtered ? `${filtered.length} deleghe` : "Caricamento..."}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportFile("xlsx")} data-testid="client-export-excel-button">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => exportFile("pdf")} data-testid="client-export-pdf-button">
            <FileText className="w-4 h-4 mr-1.5" />
            PDF
          </Button>
        </div>
      </div>

      {items === null ? (
        <p className="text-sm text-muted-foreground py-16 text-center">Caricamento documenti...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-16 text-center" data-testid="client-f24-empty">
          <FileText className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Nessun F24 corrisponde ai filtri selezionati.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="client-f24-list">
          {sorted.map((f, i) => (
            <div
              key={f.id}
              data-testid={`client-f24-card-${f.id}`}
              className="bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow duration-200 p-5 sm:p-6 animate-fade-up"
              style={{ animationDelay: `${i * 50}ms` }}
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-3 mb-1.5">
                    <h3 className="font-display text-lg font-bold text-navy">
                      F24 {MESI[f.month]} {f.year}
                      {f.categoria ? <span className="text-[#3C763D]"> · {f.categoria}</span> : null}
                    </h3>
                    <StatusBadge status={f.status} />
                    {isScaduto(f) && (
                      <span data-testid={`scaduto-badge-${f.id}`} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-50 text-red-700 border border-red-200">
                        Scaduto
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground font-mono truncate">{f.original_filename}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 mt-2 text-sm">
                    <p><span className="text-muted-foreground">Scadenza:</span> <strong>{fmtDate(f.due_date)}</strong></p>
                    <p><span className="text-muted-foreground">Importo:</span> <strong className="text-gold">{f.amount != null ? fmtEuro(f.amount) : "—"}</strong></p>
                  </div>
                  {f.status === "autorizzato" && f.authorized_at && (
                    <div
                      data-testid={`legal-audit-badge-${f.id}`}
                      className="mt-3 inline-flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5" />
                        Conferma registrata il {fmtDateTime(f.authorized_at)}
                      </span>
                      <span>IP: {f.authorized_ip}</span>
                    </div>
                  )}
                  {f.status === "pagato" && f.paid_at && (
                    <p className="mt-3 text-xs text-emerald-700 font-medium">Pagato il {fmtDate(f.paid_at)}</p>
                  )}
                </div>

                <div className="flex flex-col sm:flex-row lg:flex-col gap-2 shrink-0">
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openPdf(f)} data-testid={`f24-view-${f.id}`}>
                      <Eye className="w-4 h-4 mr-1.5" />
                      Visualizza
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => downloadPdf(f)} data-testid={`f24-download-${f.id}`}>
                      <Download className="w-4 h-4 mr-1.5" />
                      Scarica
                    </Button>
                  </div>
                  {f.status !== "pagato" && (
                    <label
                      className="flex items-start gap-2.5 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 cursor-pointer hover:border-gold transition-colors duration-200"
                      data-testid={`f24-authorize-label-${f.id}`}
                    >
                      <Checkbox
                        checked={f.status === "autorizzato"}
                        disabled={(f.status === "autorizzato" && giorniAllaScadenza(f) <= 2) || (f.status !== "autorizzato" && giorniAllaScadenza(f) <= 0)}
                        onCheckedChange={(checked) => setConfirmAuth({ f, authorize: !!checked })}
                        className="mt-0.5"
                        data-testid={`f24-authorize-checkbox-${f.id}`}
                      />
                      <span className="text-xs leading-snug text-slate-700">
                        Autorizzo espressamente lo Studio Castelli a procedere al pagamento del Modello F24
                      </span>
                    </label>
                  )}
                  {f.status === "autorizzato" && giorniAllaScadenza(f) <= 2 && (
                    <p className="text-[11px] text-amber-700 font-medium" data-testid={`revoca-bloccata-note-${f.id}`}>
                      Revoca online non più disponibile (scadenza a meno di 2 giorni): contatta lo studio per la procedura di emergenza.
                    </p>
                  )}
                  {f.status !== "autorizzato" && f.status !== "pagato" && giorniAllaScadenza(f) <= 0 && (
                    <p className="text-[11px] text-red-600 font-medium" data-testid={`autorizzazione-bloccata-note-${f.id}`}>
                      Autorizzazione non più disponibile dal giorno della scadenza: contatta lo studio per la procedura di emergenza.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!pdfModal} onOpenChange={() => { if (pdfModal?.url) URL.revokeObjectURL(pdfModal.url); setPdfModal(null); }}>
        <DialogContent className="max-w-4xl" data-testid="pdf-viewer-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-navy text-base">{pdfModal?.f?.original_filename}</DialogTitle>
          </DialogHeader>
          {pdfLoading || !pdfModal?.url ? (
            <p className="text-sm text-muted-foreground py-24 text-center">Caricamento PDF...</p>
          ) : (
            <iframe src={pdfModal.url} title="Anteprima F24" className="w-full h-[70vh] rounded-lg border border-slate-200" data-testid="pdf-viewer-iframe" />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmAuth} onOpenChange={() => setConfirmAuth(null)}>
        <AlertDialogContent data-testid="authorize-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-navy">
              {confirmAuth?.authorize ? "Conferma autorizzazione al pagamento" : "Revoca autorizzazione"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <p>
                  {confirmAuth?.authorize
                    ? `Stai autorizzando lo Studio Castelli a procedere al pagamento del Modello F24 di ${MESI[confirmAuth?.f?.month]} ${confirmAuth?.f?.year}.`
                    : "Stai revocando l'autorizzazione al pagamento precedentemente concessa."}
                </p>
                {confirmAuth?.authorize && (
                  <p className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                    La spunta ha valore legale di conferma formale. Il sistema registra data, ora,
                    indirizzo IP e identificativo del tuo account a dimostrazione della volontà espressa.
                    L'autorizzazione può essere revocata online solo fino a 2 giorni prima della scadenza.
                  </p>
                )}
                {confirmAuth?.authorize && giorniAllaScadenza(confirmAuth.f) <= 2 && (
                  <p className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 font-medium" data-testid="authorize-late-warning">
                    Attenzione: mancano meno di 2 giorni alla scadenza. Lo studio potrebbe non riuscire a completare il pagamento entro la data di scadenza.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="authorize-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAuthorize}
              data-testid="authorize-confirm"
              className={confirmAuth?.authorize ? "bg-navy hover:bg-navy-light" : "bg-amber-600 hover:bg-amber-700"}
            >
              {confirmAuth?.authorize ? "Confermo e autorizzo" : "Revoca autorizzazione"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ClientLayout>
  );
}
